import { type FC, useState } from "react";
import {
  Toolbar,
  Checkbox,
  Button,
  Typography,
  Divider,
  Menu,
} from "@mui/material";
import { useSnackbar } from "notistack";
import {
  ActionMenuItem,
  ToolbarProgress,
  useConfirmAction,
} from "@/shared/components";
import { useTranslation } from "react-i18next";
import {
  useUpdatePlatformMenuItemsMutation,
  useDeletePlatformMenuItemsMutation,
  type PlatformMenuItemsListRow,
} from "@/partner/entities/platformMenuItem";

interface PlatformMenuListToolbarProps {
  indeterminate: boolean;
  numSelected: number;
  nodes: PlatformMenuItemsListRow[];
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  selectedItems: { id: string }[];
  onCloseSelected: () => void;
}

export const PlatformMenuListToolbar: FC<PlatformMenuListToolbarProps> = ({
  indeterminate,
  numSelected,
  nodes,
  onSelectAllClick,
  selectedItems,
  onCloseSelected,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const { confirm, dialog: confirmDialog } = useConfirmAction();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const [updatePlatformMenuItems, { loading: updateLoading }] =
    useUpdatePlatformMenuItemsMutation({
      onCompleted: () => {
        onCloseSelected();
        enqueueSnackbar(t("platformMenu.snackbar.bulkUpdated"), {
          variant: "success",
        });
      },
      onError: (error) => {
        enqueueSnackbar(error.message, { variant: "error" });
      },
      refetchQueries: ["PlatformMenuItemsList"],
    });

  const [deletePlatformMenuItems, { loading: deleteLoading }] =
    useDeletePlatformMenuItemsMutation({
      onCompleted: () => {
        onCloseSelected();
        enqueueSnackbar(t("platformMenu.snackbar.bulkDeleted"), {
          variant: "success",
        });
      },
      onError: (error) => {
        enqueueSnackbar(error.message, { variant: "error" });
      },
      refetchQueries: ["PlatformMenuItemsList"],
    });

  const onPublishSelected = () => {
    updatePlatformMenuItems({
      variables: {
        ids: selectedItems.map((item) => item.id),
        input: {
          publishAt: new Date().toISOString(),
        },
      },
    });
  };

  const onUnpublishSelected = () => {
    updatePlatformMenuItems({
      variables: {
        ids: selectedItems.map((item) => item.id),
        input: {
          publishAt: null,
        },
      },
    });
  };

  const onDeleteSelected = () => {
    // До этой правки массовое удаление здесь шло вообще без вопроса.
    confirm({
      title: t("common.actions.delete"),
      text: t("common.toolbar.deleteConfirm"),
      onConfirm: () =>
        deletePlatformMenuItems({
          variables: {
            ids: selectedItems.map((item) => item.id),
          },
        }),
    });
  };

  return (
    <Toolbar sx={{ position: "relative" }}>
      <Checkbox
        color="default"
        indeterminate={indeterminate}
        checked={numSelected === nodes.length}
        onChange={onSelectAllClick}
        sx={{
          marginLeft: "-16px",
          marginRight: {
            xs: 0,
            sm: "16px",
          },
        }}
      />

      <Typography>
        {t("common.toolbar.selectedCount", { count: numSelected })}
      </Typography>

      <Divider
        orientation="vertical"
        variant="middle"
        sx={{
          mx: 1.25,
          height: "2em",
        }}
      />

      <Button
        color="inherit"
        onClick={handleClick}
        disabled={numSelected === 0}
      >
        {t("common.actions.actions")}
      </Button>

      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        <ActionMenuItem
          disabled={numSelected === 0}
          loading={updateLoading}
          onClick={() => {
            onPublishSelected();
            handleClose();
          }}
        >
          {t("common.actions.publish")}
        </ActionMenuItem>

        <ActionMenuItem
          disabled={numSelected === 0}
          loading={updateLoading}
          onClick={() => {
            onUnpublishSelected();
            handleClose();
          }}
        >
          {t("common.actions.unpublish")}
        </ActionMenuItem>

        <ActionMenuItem
          disabled={numSelected === 0}
          loading={deleteLoading}
          onClick={() => {
            onDeleteSelected();
            handleClose();
          }}
        >
          {t("common.actions.delete")}
        </ActionMenuItem>
      </Menu>

      <ToolbarProgress loading={updateLoading || deleteLoading} />
      {confirmDialog}
    </Toolbar>
  );
};
